'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { sendPasswordResetEmail } from '@/lib/email';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const { signIn } = useAuth();
  const router = useRouter();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoginError('');
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      // Fetch role from user_profiles table (authoritative source)
      let role = result?.user?.user_metadata?.role ?? 'user';
      if (result?.user?.id) {
        const supabase = createClient();
        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', result.user.id)
          .single();
        if (profileData?.role) {
          role = profileData.role;
        }
      }
      router.refresh();
      if (role === 'admin') {
        router.push('/admin-dashboard');
      } else if (role === 'artist') {
        router.push('/artist-dashboard');
      } else {
        router.push('/user-dashboard');
      }
    } catch (err: any) {
      setLoginError(err?.message || 'Invalid email or password. Please try again.');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    setResetLoading(true);
    setResetError('');
    try {
      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? window.location.origin;
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${siteUrl}/auth/callback?type=recovery`,
      });
      if (error) throw error;
      // Send branded email via Resend
      await sendPasswordResetEmail(
        resetEmail,
        `${siteUrl}/auth/callback?type=recovery`,
        resetEmail.split('@')[0]
      );
      setResetSent(true);
    } catch (err: any) {
      setResetError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  if (resetMode) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-1">Reset your password</h1>
          <p className="text-muted-foreground text-sm">Enter your email and we'll send you a reset link</p>
        </div>

        {resetSent ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-positive/10 border border-positive/20 flex items-center justify-center mx-auto mb-4">
              <Icon name="EnvelopeIcon" size={24} variant="outline" className="text-positive" />
            </div>
            <h2 className="text-base font-semibold text-foreground mb-2">Check your inbox</h2>
            <p className="text-muted-foreground text-sm mb-6">
              We sent a password reset link to <strong className="text-foreground">{resetEmail}</strong>.
            </p>
            <button onClick={() => { setResetMode(false); setResetSent(false); setResetEmail(''); }} className="btn-primary w-full py-3">
              Back to Sign In
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            {resetError && (
              <div className="px-4 py-3 rounded-lg bg-negative/10 border border-negative/20 text-negative text-sm">
                {resetError}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Email address</label>
              <input
                type="email"
                className="input-field"
                placeholder="you@example.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" disabled={resetLoading} className="btn-primary w-full py-3 text-base">
              {resetLoading ? (
                <>
                  <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
            <button type="button" onClick={() => setResetMode(false)} className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center">
              ← Back to Sign In
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-1">Welcome back</h1>
        <p className="text-muted-foreground text-sm">Sign in to your TryInk account</p>
      </div>

      {loginError && (
        <div className="mb-5 px-4 py-3 rounded-lg bg-negative/10 border border-negative/20 text-negative text-sm">
          {loginError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Email address</label>
          <input
            type="email"
            className={`input-field ${errors.email ? 'border-negative focus:ring-negative' : ''}`}
            placeholder="you@example.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && <p className="mt-1.5 text-xs text-negative">{errors.email.message}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium text-foreground">Password</label>
            <button type="button" onClick={() => setResetMode(true)} className="text-xs text-primary hover:underline">Forgot password?</button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className={`input-field pr-10 ${errors.password ? 'border-negative focus:ring-negative' : ''}`}
              placeholder="Your password"
              {...register('password', { required: 'Password is required' })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" />
            </button>
          </div>
          {errors.password && <p className="mt-1.5 text-xs text-negative">{errors.password.message}</p>}
        </div>

        <div className="flex items-center gap-2.5">
          <input
            type="checkbox"
            id="rememberMe"
            className="w-4 h-4 rounded border-border bg-input accent-primary"
            {...register('rememberMe')}
          />
          <label htmlFor="rememberMe" className="text-sm text-muted-foreground cursor-pointer">
            Remember me for 30 days
          </label>
        </div>

        <button type="submit" disabled={isLoading} className="btn-primary w-full py-3 text-base">
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Don&apos;t have an account?{' '}
        <button onClick={onSwitchToSignup} className="text-primary hover:underline font-medium">
          Sign up free
        </button>
      </p>
    </div>
  );
}